import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { combineLatest, Subscription } from 'rxjs';

import { OnboardingPhase, OnboardingState, OnboardingTask } from '../../interfaces/onboarding.interfaces';
import { OnboardingService } from '../../services/onboarding.service';
import { OnboardingAnalyticsService } from '../../services/onboarding-analytics.service';
import { OnboardingModalService } from '../../services/onboarding-modal.service';

@Component({
    selector: 'fina-onboarding-navbar-progress',
    templateUrl: './onboarding-navbar-progress.component.html',
    styleUrls: ['./onboarding-navbar-progress.component.scss'],
})
export class OnboardingNavbarProgressComponent implements OnInit, OnDestroy {
    @Input() phase: OnboardingPhase | null = null;
    @Input() heading = 'Descubre todo lo que Fina puede hacer';
    @Input() variant: 'full' | 'compact' = 'full';

    private readonly progressRingRadius = 15.5;
    private subscription?: Subscription;
    private onboarding: OnboardingState | null = null;

    constructor(private onboardingService: OnboardingService, private onboardingModalService: OnboardingModalService, private router: Router, private analytics: OnboardingAnalyticsService) {}

    ngOnInit(): void {
        if (this.phase) return;

        // `completionHold$` (FINA-3431) solo dispara un nuevo ciclo de detección al liberarse la retención.
        this.subscription = combineLatest([this.onboardingService.state$, this.onboardingService.completionHold$]).subscribe(([onboarding]) => {
            this.onboarding = onboarding;
            this.phase = this.onboardingService.getActivePhase(onboarding);
        });
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }

    get tasks(): OnboardingTask[] {
        return this.phase?.tasks || [];
    }

    get completedTasks(): number {
        return this.onboardingService.getCompletedCount(this.tasks);
    }

    /** Progreso de la FASE ACTIVA (métrica que muestra el anillo/label). QA pidió revertir el global de
     *  FINA-2760 a este valor por fase. */
    get completionPercent(): number {
        // Retención del cierre (FINA-3431): con el onboarding ya cerrado el anillo marca 100% hasta cerrar el modal
        // final, aunque la fase cierre por vencimiento o migración con tareas sin hacer (AC-02).
        if (this.onboardingService.isCompletionHeld && this.onboarding?.completion?.completed) return 100;
        return this.onboardingService.getCompletionPercent(this.tasks);
    }

    /**
     * Progreso GLOBAL del backend (`state.progress.overall.global`). Se usa **solo** para el auto-ocultamiento
     * del chip al 100% (AC3): el indicador debe retirarse cuando TODO el onboarding está completo, aunque la
     * métrica visible sea la de la fase activa.
     */
    get globalProgress(): number {
        return Math.round(this.onboarding?.progress?.overall?.global ?? 0);
    }

    /** El chip se retira con el onboarding cerrado por el backend (FINA-3377) o, sin ese dato, al 100% global;
     *  nunca mientras dura la retención del cierre (FINA-3431), para que se vea al 100% con el modal final. */
    get isOnboardingComplete(): boolean {
        if (this.onboardingService.isCompletionHeld) return false;
        return !!this.onboarding?.completion?.completed || this.globalProgress >= 100;
    }

    get progressStrokeDasharray(): string {
        const circumference = 2 * Math.PI * this.progressRingRadius;
        const filled = (this.completionPercent / 100) * circumference;
        return `${filled} ${circumference}`;
    }

    get visibleTasks(): OnboardingTask[] {
        return this.tasks.slice(0, 6);
    }

    get isCompact(): boolean {
        return this.variant === 'compact';
    }

    openDetail(): void {
        // Click en la barra del navbar que abre el checklist → dos eventos del PRD.
        // `completion_percentage` = progreso de la fase activa (estándar previo a FINA-2760).
        this.analytics.navbarBarClicked(this.completionPercent);
        this.analytics.checklistOpened('navbar_bar');
        this.onboardingModalService.openPhaseDetail();
    }

    goToTask(task: OnboardingTask): void {
        if (!task.link) return;
        this.analytics.stepClicked(task);
        this.router.navigate([task.link]);
    }
}
